import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { createPortal } from 'react-dom';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import axios from 'axios';
import { 
  BuildingOfficeIcon, 
  CheckCircleIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { clearBrandAssetsCache } from '../../../services/brandAssets';
import addAppsSvg from '../../../assets/add-apps.svg';

// Proxy URL function for S3 URLs (same as Sidebar)
const getProxiedImageUrl = (s3Url) => {
  if (!s3Url) return null;
  
  // If it's already not an S3 URL, just return it as is
  if (!s3Url.includes('amazonaws.com')) {
    return s3Url;
  }
  
  // Extract file key from the S3 URL (everything after the bucket name)
  const urlParts = s3Url.split('/');
  const fileKey = urlParts.slice(3).join('/');
  
  // Return URL that will be handled by our backend proxy endpoint
  return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
};

// Component for org icon with fallback
const OrgIcon = ({ org, darkMode }) => {
  const [imageError, setImageError] = useState(false);
  
  // Use brandMark for the icon (same as Sidebar)
  const brandIcon = org.brandAssets?.brandMark?.[darkMode ? 'white' : 'black'];
  const proxiedUrl = brandIcon ? getProxiedImageUrl(brandIcon) : null;
  
  if (proxiedUrl && !imageError) {
    return (
      <img 
        src={proxiedUrl}
        alt={org.nickname}
        className="w-8 h-8 object-contain"
        onError={() => setImageError(true)}
      />
    );
  }
  
  return <BuildingOfficeIcon className={`w-6 h-6 ${darkMode ? 'text-[#737373]' : 'text-[#676767]'}`} />;
};

const OrganizationsManagementSection = ({ user, setUser }) => {
  const { darkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [activeOrganizationId, setActiveOrganizationId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgLocation, setNewOrgLocation] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingToOrg, setSwitchingToOrg] = useState(null);

  const formatBadgeLabel = (value) => {
    if (!value) return '';
    return value
      .toString()
      .split(/[-_]/g)
      .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : ''))
      .join(' ');
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No auth token found');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/organizations`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setOrganizations(response.data.organizations);
        setActiveOrganizationId(response.data.activeOrganizationId);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchOrganization = async (organizationId) => {
    if (organizationId === activeOrganizationId) {
      return; // Already active
    }

    try {
      // Find the organization being switched to for display
      const targetOrg = organizations.find(org => org.id === organizationId);
      setSwitchingToOrg(targetOrg);
      setIsSwitching(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/user/organizations/switch/${organizationId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Update local state
        setActiveOrganizationId(organizationId);

        // Update user data in localStorage and parent component
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = {
          ...storedUser,
          ...response.data.user,
          company: {
            name: response.data.organization.name,
            nickname: response.data.organization.nickname,
            subscriptionPlan: response.data.organization.subscriptionPlan,
            customDomain: response.data.organization.customDomain,
            brandAssets: response.data.organization.brandAssets
          }
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        if (setUser) {
          setUser(updatedUser);
        }

        // Clear brand assets cache since we're switching organizations
        clearBrandAssetsCache();
        
        // Clear sidebar logo cache as well
        localStorage.removeItem('sidebarLogoCache');

        // Don't show toast - we're reloading immediately anyway
        // toast.success(`Switched to ${response.data.organization.nickname}`);
        
        // Reload the page immediately to refresh all organization-specific data
        window.location.reload();
      }
    } catch (error) {
      console.error('Error switching organization:', error);
      
      // Check if user needs to set up 2FA first
      if (error.response?.data?.requires2FA && error.response?.data?.requiresSetup) {
        toast.error('You need to enable two-factor authentication to access organizations with 2FA requirements. Please set it up in your account settings.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to switch organization');
      }
      setIsSwitching(false); // Only reset if there's an error
    }
  };

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim()) {
      toast.error('Organization name is required');
      return;
    }

    try {
      setIsCreating(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/user/organizations/create`,
        {
          name: newOrgName.trim(),
          location: newOrgLocation.trim()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Organization created successfully');
        setShowCreateModal(false);
        setNewOrgName('');
        setNewOrgLocation('');
        
        // Refresh organizations list
        await fetchOrganizations();
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error(error.response?.data?.message || 'Failed to create organization');
    } finally {
      setIsCreating(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    return darkMode ? 'bg-[#282828] text-[#a3a3a3]' : 'bg-[#ededed] text-[#525252]';
  };

  const getPlanBadgeColor = (plan) => {
    return darkMode ? 'bg-[#282828] text-[#a3a3a3]' : 'bg-[#ededed] text-[#525252]';
  };

  return (
    <>
      {/* Full-screen loading overlay when switching - rendered as portal at body level */}
      {isSwitching && switchingToOrg && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-white dark:bg-[#111111]">
          <div className="text-center">
            {/* Brand mark with crossfade animation showing current → new org */}
            <div className="relative w-20 h-20 mx-auto mb-6">
              {/* Current org (fades out) */}
              <div className="absolute inset-0 w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden bg-[#ededed] dark:bg-[#181818] border border-[#dbdbdb] dark:border-[#333] animate-[fadeOut_1s_ease-in-out_infinite]">
                <OrgIcon org={organizations.find(org => org.id === activeOrganizationId)} darkMode={darkMode} />
              </div>
              {/* New org (fades in) */}
              <div className="absolute inset-0 w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden bg-[#ededed] dark:bg-[#181818] border border-[#dbdbdb] dark:border-[#333] animate-[fadeIn_1s_ease-in-out_infinite]">
                <OrgIcon org={switchingToOrg} darkMode={darkMode} />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-[#181818] dark:text-white mb-2">
              Switching to {switchingToOrg.nickname}
            </h2>
            <p className="text-sm text-[#737373] dark:text-[#999999]">
              Loading your workspace...
            </p>
          </div>
          <style>{`
            @keyframes fadeOut {
              0%, 100% { opacity: 1; }
              50% { opacity: 0; }
            }
            @keyframes fadeIn {
              0%, 100% { opacity: 0; }
              50% { opacity: 1; }
            }
          `}</style>
        </div>,
        document.body
      )}

      <div className="organizations-management-section flex-1 flex flex-col min-h-0 overflow-y-auto px-4 py-4" style={{ fontFamily: 'var(--chat-font-family)' }}>
      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-[#2a2a2a] p-4 animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-[#333] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="h-4 bg-gray-200 dark:bg-[#333] rounded w-24 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-[#333] rounded w-full" />
                  <div className="h-3 bg-gray-200 dark:bg-[#333] rounded w-3/4 mt-1" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Organizations List */}
      {!isLoading && (
        <>
          <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-3">
              {organizations.map((org) => {
              const isActive = org.id === activeOrganizationId;
              
              return (
                <div
                  key={org.id}
                  className="rounded-xl border border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#222] p-4 transition-colors hover:border-gray-300 dark:hover:border-[#333]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-[#282828] border border-gray-200 dark:border-[#333] flex-shrink-0 overflow-hidden p-2">
                        <OrgIcon org={org} darkMode={darkMode} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-[#181818] dark:text-white truncate" style={{ fontFamily: 'var(--chat-font-family)' }}>
                            {org.nickname}
                          </span>
                          {isActive && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                              Current
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(org.role)}`}>
                            {formatBadgeLabel(org.role)}
                          </span>
                          {org.subscriptionPlan && org.subscriptionPlan !== 'none' && (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getPlanBadgeColor(org.subscriptionPlan)}`}>
                              {formatBadgeLabel(org.subscriptionPlan)}
                            </span>
                          )}
                        </div>
                        {org.customDomain && (
                          <p className="text-xs text-[#737373] dark:text-[#999999] mt-0.5 truncate" style={{ fontFamily: 'var(--chat-font-family)' }}>
                            {org.customDomain}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {!isActive && (
                    <button
                      onClick={() => handleSwitchOrganization(org.id)}
                      disabled={isSwitching}
                      className="mt-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-white dark:text-[#181818] bg-[#181818] dark:bg-[#e0e0e0] hover:bg-[#262626] dark:hover:bg-[#ededed] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    >
                      {isSwitching ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        'Switch'
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Create New Organization Button - pinned at bottom */}
          <div className="flex-shrink-0 pt-4 mt-4 border-t border-gray-200 dark:border-[#2a2a2a]">
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[#404040] dark:text-[#cccccc] bg-gray-100 dark:bg-[#252525] hover:bg-gray-200 dark:hover:bg-[#333] rounded-xl transition-colors"
              style={{ fontFamily: 'var(--chat-font-family)' }}
            >
              <img src={addAppsSvg} alt="" className="w-4 h-4 opacity-70 dark:opacity-80 dark:invert" />
              Create new organization
            </button>
          </div>
        </>
      )}

      {/* Create Organization Modal */}
      {showCreateModal && createPortal(
        <div className="fixed inset-0 z-[10002] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
            onClick={() => { setShowCreateModal(false); setNewOrgName(''); setNewOrgLocation(''); }}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-md bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-xl border border-[#dbdbdb] dark:border-[#2a2a2a] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { setShowCreateModal(false); setNewOrgName(''); setNewOrgLocation(''); }}
              className="absolute top-4 right-4 p-2 text-[#737373] hover:text-[#404040] dark:hover:text-[#cccccc] hover:bg-[#ededed] dark:hover:bg-[#252525] rounded-lg transition-colors z-10"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="p-6 pt-8">
              <h3 className="text-xl font-semibold text-[#181818] dark:text-white mb-6" style={{ fontFamily: 'var(--chat-font-family)' }}>
                Create organization
              </h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label htmlFor="orgName" className="block text-sm font-medium text-[#404040] dark:text-[#cccccc] mb-1.5" style={{ fontFamily: 'var(--chat-font-family)' }}>
                    Organization name *
                  </label>
                  <input
                    type="text"
                    id="orgName"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    placeholder="My Organization"
                    className="w-full px-3 py-2 text-sm bg-[#ededed] dark:bg-[#252525] border-0 rounded-lg text-[#181818] dark:text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#d4d4d4]/60 dark:focus:ring-[#737373]/40"
                    style={{ fontFamily: 'var(--chat-font-family)' }}
                    required
                    disabled={isCreating}
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="orgLocation" className="block text-sm font-medium text-[#404040] dark:text-[#cccccc] mb-1.5" style={{ fontFamily: 'var(--chat-font-family)' }}>
                    Location (optional)
                  </label>
                  <input
                    type="text"
                    id="orgLocation"
                    value={newOrgLocation}
                    onChange={(e) => setNewOrgLocation(e.target.value)}
                    placeholder="New York, NY"
                    className="w-full px-3 py-2 text-sm bg-[#ededed] dark:bg-[#252525] border-0 rounded-lg text-[#181818] dark:text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#d4d4d4]/60 dark:focus:ring-[#737373]/40"
                    style={{ fontFamily: 'var(--chat-font-family)' }}
                    disabled={isCreating}
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setNewOrgName(''); setNewOrgLocation(''); }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-[#525252] dark:text-[#999999] bg-[#ededed] dark:bg-[#252525] hover:bg-[#e0e0e0] dark:hover:bg-[#333] rounded-lg transition-colors disabled:opacity-50"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateOrganization}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white dark:text-[#181818] bg-[#181818] dark:bg-[#e0e0e0] hover:bg-[#262626] dark:hover:bg-[#ededed] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={isCreating || !newOrgName.trim()}
                >
                  {isCreating ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white dark:border-[#181818]/30 dark:border-t-[#181818] rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
    </>
  );
};

export default OrganizationsManagementSection;
